import type { ScheduleStrategySummary } from '../types/schedule';
import { formatTradingType } from './scheduleSummary';

/**
 * Per-column filter state for the Schedule table. Every header-cell filter
 * owns one of these sets; an empty set means "no filter for this column".
 *
 * Semantics: AND across columns, OR within each column. A strategy passes
 * only when every active filter admits it.
 */
export type ScheduleStatusFilter =
  | 'OPEN'
  | 'PENDING'
  | 'ORDERS'
  | 'ERRORS'
  | 'CANCELLED'
  | 'CLOSED';

/**
 * State-column predicates. Three coarse buckets that match the single-chip
 * verdict rendered in the new State column. Independent from the six-bucket
 * Health filter — the user can combine both (e.g. State=RUNNING +
 * Health=Open Orders to find strategies that are running but have stuck
 * unfilled orders).
 */
export type ScheduleStateFilter =
  | 'RUNNING'
  | 'CLOSED'
  | 'ERROR';

export interface ScheduleColumnFilters {
  /** basket_id values that should be kept. */
  baskets: Set<string>;
  /** State-column predicates — single-chip verdict. */
  state: Set<ScheduleStateFilter>;
  /** Health-column predicates (same box-aligned model as before). */
  status: Set<ScheduleStatusFilter>;
  /** Underlying symbols. */
  underlyings: Set<string>;
  /** expiry_type values (e.g. WEEKLY / MONTHLY). */
  expiries: Set<string>;
  /** product_type values (MIS / NRML). */
  products: Set<string>;
  /** Composite trading-type display string (what the cell renders). */
  tradingTypes: Set<string>;
}

export function emptyScheduleFilters(): ScheduleColumnFilters {
  return {
    baskets: new Set(),
    state: new Set(),
    status: new Set(),
    underlyings: new Set(),
    expiries: new Set(),
    products: new Set(),
    tradingTypes: new Set(),
  };
}

export function isAnyColumnFilterActive(f: ScheduleColumnFilters): boolean {
  return f.baskets.size > 0
    || f.state.size > 0
    || f.status.size > 0
    || f.underlyings.size > 0
    || f.expiries.size > 0
    || f.products.size > 0
    || f.tradingTypes.size > 0;
}

/**
 * Serialise the column-filter Sets to a JSON-safe object for localStorage
 * round-trips. Each `Set<string>` becomes a sorted string array (sorted only
 * for stable comparison across reloads).
 */
export function serializeScheduleFilters(f: ScheduleColumnFilters): {
  baskets: string[]; state: string[]; status: string[];
  underlyings: string[]; expiries: string[]; products: string[]; tradingTypes: string[];
} {
  return {
    baskets:      Array.from(f.baskets).sort(),
    state:        Array.from(f.state).sort(),
    status:       Array.from(f.status).sort(),
    underlyings:  Array.from(f.underlyings).sort(),
    expiries:     Array.from(f.expiries).sort(),
    products:     Array.from(f.products).sort(),
    tradingTypes: Array.from(f.tradingTypes).sort(),
  };
}

/**
 * Parse a localStorage JSON blob back into `ScheduleColumnFilters`. Tolerant
 * of missing keys, wrong types, or partial shapes — anything off-spec falls
 * back to an empty Set so a corrupted preference can't crash the page.
 */
export function deserializeScheduleFilters(raw: unknown): ScheduleColumnFilters {
  const empty = emptyScheduleFilters();
  if (!raw || typeof raw !== 'object') return empty;
  const obj = raw as Record<string, unknown>;
  const pickStringSet = <T extends string>(key: string): Set<T> => {
    const v = obj[key];
    if (!Array.isArray(v)) return new Set();
    return new Set(v.filter((x): x is T => typeof x === 'string'));
  };
  return {
    baskets:      pickStringSet('baskets'),
    state:        pickStringSet<ScheduleStateFilter>('state'),
    status:       pickStringSet<ScheduleStatusFilter>('status'),
    underlyings:  pickStringSet('underlyings'),
    expiries:     pickStringSet('expiries'),
    products:     pickStringSet('products'),
    tradingTypes: pickStringSet('tradingTypes'),
  };
}

// ── Status predicates (box-aligned, non-exclusive) ──────────────────────────

export interface StatusOption {
  value: ScheduleStatusFilter;
  label: string;
  /** Tailwind background class for the coloured dot. */
  dotClass: string;
  /** Tailwind text colour that matches the box — applied to the label so each
   *  row in the Health filter popover picks up its semantic colour, making
   *  the list scannable at a glance. */
  labelClass: string;
}

export const STATUS_OPTIONS: ReadonlyArray<StatusOption> = [
  { value: 'OPEN',      label: 'Open',         dotClass: 'bg-blue-400',   labelClass: 'text-blue-700 dark:text-blue-300'     },
  { value: 'PENDING',   label: 'Pending',      dotClass: 'bg-yellow-400', labelClass: 'text-yellow-700 dark:text-yellow-300' },
  { value: 'ORDERS',    label: 'Open Orders',  dotClass: 'bg-orange-400', labelClass: 'text-orange-700 dark:text-orange-300' },
  { value: 'ERRORS',    label: 'Errors',       dotClass: 'bg-red-500',    labelClass: 'text-red-700 dark:text-red-400'       },
  { value: 'CANCELLED', label: 'Cancelled',    dotClass: 'bg-slate-400',  labelClass: 'text-slate-700 dark:text-slate-300'   },
  { value: 'CLOSED',    label: 'Closed',       dotClass: 'bg-gray-400',   labelClass: 'text-gray-700 dark:text-gray-300'     },
];

function matchesStatusValue(s: ScheduleStrategySummary, v: ScheduleStatusFilter): boolean {
  const e = s.entry_counts;
  const x = s.exit_counts;
  const sl = s.sl_counts;
  const slCancelled = sl.cancelled ?? 0;
  switch (v) {
    case 'OPEN':      return s.open_count > 0;
    case 'PENDING':   return e.pending > 0;
    case 'ORDERS':    return e.open + sl.open + x.open > 0;
    case 'ERRORS':    return e.rejected + sl.rejected + x.rejected > 0;
    case 'CANCELLED': return e.cancelled + slCancelled + x.cancelled > 0;
    case 'CLOSED':    return s.closed_count > 0;
  }
}

// ── State predicates (3-bucket collapsed verdict) ───────────────────────────

export interface StateOption {
  value: ScheduleStateFilter;
  label: string;
  /** Tailwind background class for the coloured dot. */
  dotClass: string;
  /** Tailwind text colour applied to the label so each row in the State filter
   *  popover picks up its semantic colour. */
  labelClass: string;
}

export const STATE_OPTIONS: ReadonlyArray<StateOption> = [
  { value: 'RUNNING', label: 'Running', dotClass: 'bg-green-500', labelClass: 'text-green-700 dark:text-green-300' },
  { value: 'ERROR',   label: 'Error',   dotClass: 'bg-red-500',   labelClass: 'text-red-700 dark:text-red-400'      },
  { value: 'CLOSED',  label: 'Closed',  dotClass: 'bg-gray-400',  labelClass: 'text-gray-700 dark:text-gray-300'    },
];

/**
 * Filter predicate for the State column. Must agree with the priority logic
 * in `computeStrategyRunState` (scheduleChip.ts) so the dropdown bucket and
 * the rendered chip never disagree.
 *
 * Priority (first match wins): ERROR → RUNNING → CLOSED → SCHEDULED.
 * Critically, RUNNING is checked before CLOSED so that re-entry / TP_REEXEC
 * children armed after parent closures keep the row in the RUNNING bucket.
 *
 * SCHEDULED rows (no positions yet) are intentionally NOT a filter bucket —
 * an empty filter selection already keeps them visible, and exposing
 * SCHEDULED as a fourth chip would clutter the popover.
 */
function matchesStateValue(s: ScheduleStrategySummary, v: ScheduleStateFilter): boolean {
  const e = s.entry_counts;
  const x = s.exit_counts;
  const sl = s.sl_counts;

  const rejected = e.rejected + x.rejected + sl.rejected;
  const stuckOpen = e.open + x.open + sl.open;
  const isError = rejected > 0 || stuckOpen > 0;
  if (v === 'ERROR') return isError;
  if (isError) return false;  // ERROR locks out the lower-priority buckets

  // RUNNING — live legs OR anything armed (entry/exit/SL pending). Includes
  // SL_REENTRY / SL_RECOST / SL_REEXEC / TP_REEXEC children whose
  // entry_status is TRIGGER_PENDING (aggregated into entry_counts.pending).
  const armedPending = e.pending + x.pending + sl.pending;
  const isRunning = s.open_count > 0 || armedPending > 0;
  if (v === 'RUNNING') return isRunning;
  if (isRunning) return false;

  // CLOSED — anything that has terminally finished but isn't running.
  // Either real closures (closed_count > 0) or cancelled-only.
  const hasCancellation = e.cancelled > 0 || x.cancelled > 0 || (sl.cancelled ?? 0) > 0;
  const isClosed = s.closed_count > 0 || hasCancellation;
  if (v === 'CLOSED') return isClosed;
  return false;
}

// ── Row-level filter entrypoint ─────────────────────────────────────────────

/**
 * Apply the Schedule table's client-side filters:
 *   • substring search across basket / strategy / underlying name
 *   • each column filter AND'd together; OR within a column
 *
 * Returns the same array reference when no filter is active so memo
 * equality holds downstream.
 */
export function applyScheduleFilters(
  strategies: ScheduleStrategySummary[],
  searchQuery: string,
  filters: ScheduleColumnFilters,
): ScheduleStrategySummary[] {
  const q = searchQuery.trim().toLowerCase();
  const hasQ = q.length > 0;
  const hasFilters = isAnyColumnFilterActive(filters);
  if (!hasQ && !hasFilters) return strategies;

  return strategies.filter((s) => {
    if (hasQ) {
      const matched =
        s.basket_name.toLowerCase().includes(q)
        || s.strategy_name.toLowerCase().includes(q)
        || s.underlying.toLowerCase().includes(q);
      if (!matched) return false;
    }
    if (filters.baskets.size > 0 && !filters.baskets.has(s.basket_id)) return false;
    if (filters.underlyings.size > 0 && !filters.underlyings.has(s.underlying)) return false;
    if (filters.expiries.size > 0 && !filters.expiries.has(s.expiry_type || '')) return false;
    if (filters.products.size > 0 && !filters.products.has(s.product_type || '')) return false;
    if (filters.tradingTypes.size > 0) {
      const key = formatTradingType(s.trading_type, s.intraday_exit_mode) || '';
      if (!filters.tradingTypes.has(key)) return false;
    }
    if (filters.status.size > 0) {
      let anyMatch = false;
      filters.status.forEach((v) => { if (!anyMatch && matchesStatusValue(s, v)) anyMatch = true; });
      if (!anyMatch) return false;
    }
    if (filters.state.size > 0) {
      let anyMatch = false;
      filters.state.forEach((v) => { if (!anyMatch && matchesStateValue(s, v)) anyMatch = true; });
      if (!anyMatch) return false;
    }
    return true;
  });
}

export function hasActiveScheduleFilters(
  searchQuery: string,
  filters: ScheduleColumnFilters,
): boolean {
  return searchQuery.trim().length > 0 || isAnyColumnFilterActive(filters);
}

// ── Distinct-value extractors (feed each filter popover) ────────────────────

export interface LabeledOption<V extends string = string> {
  value: V;
  label: string;
  count: number;
}

function sortByCountDesc<V extends string>(opts: LabeledOption<V>[]): LabeledOption<V>[] {
  return opts.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

export function collectBasketOptions(strategies: ScheduleStrategySummary[]): LabeledOption<string>[] {
  const map = new Map<string, { label: string; count: number }>();
  for (const s of strategies) {
    const cur = map.get(s.basket_id);
    if (cur) cur.count++;
    else map.set(s.basket_id, { label: s.basket_name || s.basket_id, count: 1 });
  }
  return sortByCountDesc(
    Array.from(map, ([value, v]) => ({ value, label: v.label, count: v.count })),
  );
}

function collectSingleField(
  strategies: ScheduleStrategySummary[],
  extract: (s: ScheduleStrategySummary) => string | null | undefined,
  formatLabel?: (v: string) => string,
): LabeledOption<string>[] {
  const counts = new Map<string, number>();
  for (const s of strategies) {
    const raw = extract(s);
    if (!raw) continue;
    counts.set(raw, (counts.get(raw) ?? 0) + 1);
  }
  return sortByCountDesc(
    Array.from(counts, ([value, count]) => ({
      value,
      label: formatLabel ? formatLabel(value) : value,
      count,
    })),
  );
}

export function collectUnderlyingOptions(strategies: ScheduleStrategySummary[]): LabeledOption<string>[] {
  return collectSingleField(strategies, (s) => s.underlying);
}

export function collectExpiryOptions(strategies: ScheduleStrategySummary[]): LabeledOption<string>[] {
  return collectSingleField(strategies, (s) => s.expiry_type, (v) => v.toUpperCase());
}

export function collectProductOptions(strategies: ScheduleStrategySummary[]): LabeledOption<string>[] {
  return collectSingleField(strategies, (s) => s.product_type);
}

export function collectTradingTypeOptions(strategies: ScheduleStrategySummary[]): LabeledOption<string>[] {
  return collectSingleField(strategies, (s) =>
    formatTradingType(s.trading_type, s.intraday_exit_mode) || null);
}

/** Status counts restricted to rows that already passed every OTHER filter. */
export function computeStatusCounts(
  strategies: ScheduleStrategySummary[],
): Record<ScheduleStatusFilter, number> {
  const counts: Record<ScheduleStatusFilter, number> = {
    OPEN: 0, PENDING: 0, ORDERS: 0, ERRORS: 0, CANCELLED: 0, CLOSED: 0,
  };
  for (const s of strategies) {
    for (const opt of STATUS_OPTIONS) {
      if (matchesStatusValue(s, opt.value)) counts[opt.value]++;
    }
  }
  return counts;
}

/** State counts restricted to rows that already passed every OTHER filter. */
export function computeStateCounts(
  strategies: ScheduleStrategySummary[],
): Record<ScheduleStateFilter, number> {
  const counts: Record<ScheduleStateFilter, number> = {
    RUNNING: 0, CLOSED: 0, ERROR: 0,
  };
  for (const s of strategies) {
    for (const opt of STATE_OPTIONS) {
      if (matchesStateValue(s, opt.value)) counts[opt.value]++;
    }
  }
  return counts;
}
